import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { CamposDeMaquinaria } from "@/lib/informes";

import {
  GrupoDeIdentificacionDeMaquina,
  GrupoDeValorizacionDeMaquina,
  ValorComercialEstimado,
} from "./CamposDeMaquina";
import { RotuloDeGrupo } from "./CamposDeConstruccion";
import { FotosDeLaFila } from "./FotosDeLaFila";
import { camposDesde, MAQUINA_VACIA, type FormularioDeMaquina } from "./maquinaria";

interface FormularioDeNuevaMaquinaProps {
  ocupada: boolean;
  /** Con qué arranca: lo que la App ya sabe de una máquina creada en terreno. */
  inicial?: FormularioDeMaquina;
  /** Crea la fila y, con el id que devuelve, sube las fotos retenidas. */
  onAgregar: (campos: CamposDeMaquinaria, fotos: File[]) => void;
  onCerrar: () => void;
}

/**
 * El formulario EN LÍNEA de la lista: agrega una máquina nueva.
 *
 * SIEMPRE AGREGA, nunca corrige: corregir una fila abre el modal, con su propio
 * estado, así que un borrador a medio escribir acá no se pierde al abrir otra
 * fila. Se desmonta al cerrar, y con él se va el borrador y las fotos
 * retenidas — "Cancelar" promete eso.
 *
 * SOLO EL NOMBRE ES OBLIGATORIO: la valorización puede quedar para después, y
 * una fila sin valor comercial es justo lo que la lista marca «Sin valorizar».
 *
 * LAS FOTOS SE RETIENEN en el navegador y viajan con el id que devuelve el
 * alta (`onAgregar`): hasta ahí no hay fila a la cual colgarlas.
 */
export function FormularioDeNuevaMaquina({
  ocupada,
  inicial,
  onAgregar,
  onCerrar,
}: FormularioDeNuevaMaquinaProps) {
  const [formulario, setFormulario] = useState<FormularioDeMaquina>(inicial ?? MAQUINA_VACIA);
  const [fotos, setFotos] = useState<File[]>([]);
  const [rechazoDeFoto, setRechazoDeFoto] = useState<string | null>(null);

  const puedeAgregar = formulario.nombre.trim() !== "";

  function escribir(cambio: Partial<FormularioDeMaquina>) {
    setFormulario((actual) => ({ ...actual, ...cambio }));
  }

  function agregar() {
    if (!puedeAgregar) return;

    onAgregar(camposDesde(formulario), fotos);
    onCerrar();
  }

  const comunes = { idPrefijo: "maquina", formulario, ocupada, escribir };

  return (
    <div
      id="nueva-maquina"
      className="bg-superficie-sutil border-divisor flex scroll-mt-5 flex-col gap-3.5 border-y-[1.5px] px-5 pt-4 pb-4.5"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <h4 className="text-ink text-[0.84375rem] font-bold">Nueva máquina o equipo</h4>
        <span className="text-ink-muted text-caption">
          {inicial
            ? "Viene de terreno con su nombre, año y unidades. Completa la valorización con sus fotos y audios."
            : "Completa al menos el nombre; la valorización puede quedar para después."}
        </span>
      </div>

      <RotuloDeGrupo>Identificación</RotuloDeGrupo>
      <GrupoDeIdentificacionDeMaquina {...comunes} requerido />

      <RotuloDeGrupo aparte="Opcional al crear">Valorización</RotuloDeGrupo>
      <GrupoDeValorizacionDeMaquina {...comunes} />

      <ValorComercialEstimado formulario={formulario} />

      <FotosDeLaFila
        titulo="Fotos de la máquina/equipo (una o más)"
        etiquetaDelSelector="Adjuntar fotos de la máquina o equipo"
        ocupada={ocupada}
        editando={false}
        pendientes={fotos}
        // Nunca corrige una fila que ya existe: `editando={false}` hace que se
        // dibuje la lista de PENDIENTES y no la de guardadas.
        guardadas={[]}
        urlDe={() => ""}
        alt="Foto de la máquina"
        error={rechazoDeFoto}
        onElegidas={(elegidos) => setFotos((actuales) => [...actuales, ...elegidos])}
        onRechazo={setRechazoDeFoto}
        onQuitarPendiente={(indice) => setFotos((actuales) => actuales.filter((_, i) => i !== indice))}
        onQuitarGuardada={() => {}}
      />

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <Button type="button" onClick={agregar} disabled={ocupada || !puedeAgregar}>
          {ocupada ? <Spinner /> : null}
          Agregar a la lista
        </Button>
        <Button type="button" variant="ghost" onClick={onCerrar}>
          Cancelar
        </Button>
        {puedeAgregar ? null : <span className="text-ink-hint text-caption">Falta: nombre</span>}
      </div>
    </div>
  );
}
